/**
 * Which step an address under the Get a Quote route names, and whether the wizard can render it yet.
 *
 * Step identifiers double as address segments, so there is nothing to translate — this module only
 * validates that a segment names a real step and decides whether the draft supports it.
 */

import { QuoteWizardStep } from './quoteWizardState';

/** Step order, which is also the order the stepper draws and the order progress is judged against. */
export const STEP_ORDER: QuoteWizardStep[] = ['business-details', 'add-customers', 'review-quote'];

/** The step `segment` names, or `null` when it names no step this wizard knows. */
export const stepForSegment = (segment: string | undefined): QuoteWizardStep | null =>
  STEP_ORDER.find((step) => step === segment) ?? null;

/** What the wizard holds, as far as deciding which steps it can render. */
interface DraftProgress {
  hasBusinessDetails: boolean;
  hasQuote: boolean;
}

interface ResolvedStep {
  step: QuoteWizardStep;
  /**
   * True when `step` is not the step the address asked for, so the caller should replace the address
   * rather than render a step the supplier did not request.
   */
  shouldRedirect: boolean;
}

/**
 * A step is reachable only when the state its body reads is present: Review Quote needs a generated
 * quote, Add Customers needs submitted business details. The draft lives in memory, so a reload or a
 * pasted link can ask for either without them.
 *
 * Anything unreachable or unrecognized resolves to Business Details, which needs nothing.
 */
export const resolveStep = (
  segment: string | undefined,
  { hasBusinessDetails, hasQuote }: DraftProgress
): ResolvedStep => {
  const requested = stepForSegment(segment);

  if (requested === null) {
    return { step: 'business-details', shouldRedirect: true };
  }

  const isReachable =
    requested === 'review-quote' ? hasQuote : requested === 'add-customers' ? hasBusinessDetails : true;

  return isReachable ? { step: requested, shouldRedirect: false } : { step: 'business-details', shouldRedirect: true };
};
